import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as svc from "../../api/appointmentService";

export const fetchAppointments = createAsyncThunk("appointments/fetchAll", async (params) => {
  const res = await svc.getAppointments(params);
  return res.data;
});

// Fetch for a date range by calling the single-date endpoint for each day
export const fetchCalendarAppointments = createAsyncThunk(
  "appointments/fetchCalendar",
  async ({ from, to }) => {
    if (!to || to === from) {
      const res = await svc.getCalendarAppointments(from);
      return Array.isArray(res.data) ? res.data : [];
    }
    // Build array of dates from from (inclusive) to to (exclusive)
    const dates = [];
    const cur = new Date(from);
    const end = new Date(to);
    while (cur < end) {
      dates.push(cur.toISOString().split("T")[0]);
      cur.setDate(cur.getDate() + 1);
    }
    const results = await Promise.allSettled(dates.map((d) => svc.getCalendarAppointments(d)));
    return results.flatMap((r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : []));
  }
);

// Surface API error message so the modal can show it to the user
export const createAppointmentThunk = createAsyncThunk(
  "appointments/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await svc.createAppointment(data);
      return res.data;
    } catch (err) {
      const msg = err?.response?.data;
      return rejectWithValue(typeof msg === "string" ? msg : (msg?.message || msg?.title || "Failed to book appointment."));
    }
  }
);

export const updateAppointmentThunk = createAsyncThunk(
  "appointments/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await svc.updateAppointment(id, data);
      return res.data;
    } catch (err) {
      const msg = err?.response?.data;
      return rejectWithValue(typeof msg === "string" ? msg : (msg?.message || msg?.title || "Failed to update appointment."));
    }
  }
);

export const deleteAppointmentThunk = createAsyncThunk("appointments/delete", async (id) => {
  await svc.deleteAppointment(id);
  return id;
});

const appointmentSlice = createSlice({
  name: "appointments",
  initialState: {
    list: [],
    calendarEvents: [],
    loading: false,
    error: null,
    selected: null,
  },
  reducers: {
    setSelectedAppointment: (state, action) => { state.selected = action.payload; },
    clearSelectedAppointment: (state) => { state.selected = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAppointments.fulfilled, (state, action) => { state.loading = false; state.list = action.payload; })
      .addCase(fetchAppointments.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
      .addCase(fetchCalendarAppointments.fulfilled, (state, action) => { state.calendarEvents = action.payload; })
      .addCase(createAppointmentThunk.fulfilled, (state, action) => {
        state.list.push(action.payload);
        if (action.payload) state.calendarEvents.push(action.payload);
      })
      .addCase(updateAppointmentThunk.fulfilled, (state, action) => {
        const id = action.payload?.appointmentID || action.payload?.id;
        const listIdx = state.list.findIndex((a) => (a.appointmentID || a.id) === id);
        if (listIdx !== -1) state.list[listIdx] = action.payload;
        const calIdx = state.calendarEvents.findIndex((a) => (a.appointmentID || a.id) === id);
        if (calIdx !== -1) state.calendarEvents[calIdx] = action.payload;
      })
      .addCase(deleteAppointmentThunk.fulfilled, (state, action) => {
        state.list = state.list.filter((a) => (a.appointmentID || a.id) !== action.payload);
        state.calendarEvents = state.calendarEvents.filter((a) => (a.appointmentID || a.id) !== action.payload);
      });
  },
});

export const { setSelectedAppointment, clearSelectedAppointment } = appointmentSlice.actions;
export default appointmentSlice.reducer;
